import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowUpRight, ShieldCheck, ArrowDown } from "lucide-react";
import { scrollToId } from "../hooks/useSmoothScroll";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b7321a98-e256-4cb1-ae2f-2d5f3d3ad9af/images/f1e7efac2679fa4911a038f2438be62d8c689b233c8b1010ec412439fea0d868.jpeg";

const slam = {
  hidden: { opacity: 0, scale: 2.3 },
  show: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: 0.15 + i * 0.14, duration: 0.3, ease: [0.7, 0, 0.84, 0] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block">
    <motion.span variants={slam} custom={i} className="block" style={{ transformOrigin: "50% 60%" }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const smoothScroll = useSpring(scrollYProgress, { stiffness: 55, damping: 18, mass: 0.6 });
  const videoY = useTransform(smoothScroll, [0, 1], [0, -820]);
  const videoScale = useTransform(smoothScroll, [0, 1], [1, 1.25]);
  const videoOpacity = useTransform(smoothScroll, [0, 0.55, 1], [1, 1, 0]);
  const streakY1 = useTransform(smoothScroll, [0, 1], [120, -1100]);
  const streakY2 = useTransform(smoothScroll, [0, 1], [260, -1500]);
  const streakY3 = useTransform(smoothScroll, [0, 1], [200, -900]);
  const streakOpacity = useTransform(smoothScroll, [0, 0.08, 0.75, 1], [0, 1, 1, 0]);
  const shakeRamp = (v) => (v < 0.05 || v > 0.6 ? 0 : v < 0.25 ? (v - 0.05) / 0.2 : (0.6 - v) / 0.35);
  const shakeX = useTransform(smoothScroll, (v) => Math.sin(v * 90) * 2.6 * shakeRamp(v));
  const shakeY = useTransform(smoothScroll, (v) => Math.cos(v * 70) * 1.8 * shakeRamp(v));

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen pt-28 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Motion background — rockets launching, shoot to top on scroll */}
      <motion.div style={{ y: videoY, scale: videoScale, opacity: videoOpacity }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <video
          ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
          className="absolute left-0 top-0 h-[125%] w-full object-cover opacity-40"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          data-testid="hero-bg-video"
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
          <source src="/hero-bg.webm" type="video/webm" />
        </video>
      </motion.div>
      {/* Cinematic thrust streaks — race ahead of the scroll */}
      <motion.div style={{ y: streakY1, opacity: streakOpacity }} className="pointer-events-none absolute left-[16%] top-full h-40 w-[2px] bg-gradient-to-t from-transparent via-[#C6FF00]/80 to-transparent blur-[0.5px]" aria-hidden="true" />
      <motion.div style={{ y: streakY2, opacity: streakOpacity }} className="pointer-events-none absolute left-[58%] top-full h-56 w-[2px] bg-gradient-to-t from-transparent via-[#C6FF00]/60 to-transparent" aria-hidden="true" />
      <motion.div style={{ y: streakY3, opacity: streakOpacity }} className="pointer-events-none absolute left-[86%] top-full h-32 w-px bg-gradient-to-t from-transparent via-white/50 to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0A0C0F] via-[#0A0C0F]/70 to-[#0A0C0F]/30" aria-hidden="true" />
      {/* Sleek geometric backdrop */}
      <div className="pointer-events-none absolute inset-0 grid-bg grid-drift" aria-hidden="true" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(52% 42% at 86% 4%, rgba(198,255,0,0.10), transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="scanline" aria-hidden="true" />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
        <motion.div
          style={{ y: textY }}
          initial="hidden"
          animate="show"
          className="lg:col-span-7"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#2A2D34] bg-[#111317] px-4 py-2 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 rounded-full bg-[#C6FF00] animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A939B]">
              Grow Bolder. Launch Faster.
            </span>
          </motion.div>

          <motion.div
            animate={{ x: [0, -11, 10, -8, 7, -4, 3, -1, 0], y: [0, 6, -5, 4, -3, 2, -1, 0, 0] }}
            transition={{ delay: 0.95, duration: 0.55, ease: "easeOut" }}
          >
          <motion.h1 style={{ x: shakeX, y: shakeY }} className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] font-black uppercase leading-[0.92] tracking-tighter">
            <Line i={0}>We build</Line>
            <Line i={1}>
              systems that
            </Line>
            <Line i={2}>
              get you <span className="text-[#C6FF00] lime-text-glow">more</span>
            </Line>
            <Line i={3}>
              <span className="text-[#C6FF00] lime-text-glow">clients.</span>
            </Line>
          </motion.h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg text-white leading-relaxed font-medium"
            data-testid="hero-subcopy"
          >
            Your strategic growth partner — building the brand, website and
            systems that turn attention into paying clients.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
              <Link
                to="/brand-audit"
                data-testid="hero-primary-cta"
                className="btn-shine group flex items-center gap-2 whitespace-nowrap rounded-full bg-white text-[#0A0C0F] font-bold uppercase text-sm tracking-wider px-6 sm:px-8 py-4 hover:bg-[#C6FF00] active:bg-[#C6FF00] transition-colors duration-300"
              >
                Get Your Free Brand Audit
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ x: 4 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
              <Link
                to="/method"
                data-testid="hero-secondary-cta"
                className="flex items-center gap-2 text-white font-bold uppercase text-sm tracking-wider px-2 py-4 border-b-2 border-[#C6FF00] hover:text-[#C6FF00] transition-colors duration-300"
              >
                See How HughLaunch Works
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 inline-flex items-center gap-2.5 text-[#8A939B]"
            data-testid="hero-trust-badge"
          >
            <ShieldCheck className="h-5 w-5 text-[#C6FF00]" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">
              Results Driven · Client Focused
            </span>
          </motion.div>
        </motion.div>

        {/* Clipped photography frame */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none absolute inset-0 translate-x-3 translate-y-3 border border-[#C6FF00]/40"
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="light-sweep relative h-[420px] lg:h-[560px] overflow-hidden border border-[#2A2D34] bg-[#0A0C0F]"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}
          >
            <motion.div style={{ y: imgY }} className="absolute inset-0 h-[120%] w-full">
              <img
                src={HERO_IMG}
                alt="Founder-led strategy session at the HughLaunch studio"
                className="animate-kenburns h-full w-full object-cover grayscale-[0.2]"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C0F] via-[#0A0C0F]/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-2xl font-black uppercase tracking-tight leading-none">
                Strategy first. <span className="text-[#C6FF00]">Built by hand.</span>
              </p>
            </div>
          </motion.div>
          <div className="absolute -top-4 -right-4 h-24 w-24 border-t-2 border-r-2 border-[#C6FF00]" />
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#8A939B] hover:text-[#C6FF00] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
};
